import {
  resolveAgentWorkbenchSidebarConfig,
  type AgentWorkbenchSidebarConfig,
  type AgentWorkbenchAgentItem,
  type AgentWorkbenchNavigationViewModel,
} from '@inf-monkeys-tech/monkeys';
import { Blocks, Bot, Pin, Plus, RefreshCw, Settings } from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { useMonkeysLocaleMessages } from '../../provider';
import { cn } from '../layout';
import { AgentWorkbenchSidebarContainer } from './AgentWorkbenchSidebarContainer';
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../primitives';
import {
  AgentWorkbenchSessionList,
  AgentWorkbenchSidebarNewSessionButton,
  AgentWorkbenchSidebarSearch,
} from './AgentWorkbenchSidebarNavigation';

export type AgentWorkbenchSidebarIntent =
  | { type: 'change-tab'; tab: 'agents' | 'sessions' }
  | { type: 'change-search'; query: string }
  | { type: 'create-agent' }
  | { type: 'create-session' }
  | { type: 'select-agent'; itemId: string }
  | { type: 'select-session'; sessionId: string }
  | { type: 'toggle-agent-pin'; itemId: string; pinned: boolean }
  | { type: 'toggle-session-pin'; sessionId: string; pinned: boolean }
  | { type: 'rename-session'; sessionId: string; title: string }
  | { type: 'delete-session'; sessionId: string }
  | { type: 'retry-agents' }
  | { type: 'retry-sessions' }
  | { type: 'open-capabilities' }
  | { type: 'open-agent-settings' };

export interface AgentWorkbenchSidebarClassNames {
  root?: string;
  search?: string;
  tabs?: string;
  body?: string;
  item?: string;
  footer?: string;
}

export interface AgentWorkbenchSidebarProps {
  viewModel: AgentWorkbenchNavigationViewModel;
  navigationMode?: 'agents-and-sessions' | 'sessions-only';
  config?: AgentWorkbenchSidebarConfig;
  header?: ReactNode;
  headerActions?: ReactNode;
  footer?: ReactNode;
  className?: string;
  classNames?: AgentWorkbenchSidebarClassNames;
  onIntent?: (intent: AgentWorkbenchSidebarIntent) => void;
}

function ItemAvatar({ item }: { item: AgentWorkbenchAgentItem }) {
  const [failed, setFailed] = useState(false);
  if (item.iconUrl && !failed) {
    return (
      <img
        src={item.iconUrl}
        alt=""
        className="size-full object-cover"
        onError={() => setFailed(true)}
      />
    );
  }
  const initial = item.name.trim().charAt(0).toLocaleUpperCase();
  return initial ? (
    <span className="text-sm font-semibold">{initial}</span>
  ) : (
    <Bot className="size-4" />
  );
}

function CollectionState({
  status,
  empty,
  noResults,
  hasItems,
  query,
  error,
  onRetry,
}: {
  status: 'loading' | 'ready' | 'error';
  empty: string;
  noResults: string;
  hasItems: boolean;
  query: string;
  error?: string;
  onRetry: () => void;
}) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.navigation;
  if (status === 'loading' && !hasItems) {
    return (
      <div className="px-3 py-8 text-center text-sm text-muted-foreground">
        {messages.loading}
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="flex flex-col items-center gap-3 px-3 py-8 text-center">
        <p
          className="max-w-full break-words text-sm text-muted-foreground"
          role="alert"
        >
          {error}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="gap-1.5 px-0 text-sm text-primary hover:text-primary"
          onClick={onRetry}
        >
          <RefreshCw className="size-3.5" aria-hidden="true" />
          {messages.retry}
        </Button>
      </div>
    );
  }
  if (!hasItems) {
    return (
      <div className="px-3 py-8 text-center text-sm text-muted-foreground">
        {query ? noResults : empty}
      </div>
    );
  }
  return null;
}

export function AgentWorkbenchSidebar({
  viewModel,
  navigationMode,
  config,
  header,
  headerActions,
  footer,
  className,
  classNames,
  onIntent,
}: AgentWorkbenchSidebarProps) {
  const messages = useMonkeysLocaleMessages().agentWorkbench.navigation;
  const resolvedConfig = useMemo(
    () => resolveAgentWorkbenchSidebarConfig(config),
    [config],
  );
  const visibleSections = useMemo(
    () => new Set(resolvedConfig.visibleSections),
    [resolvedConfig.visibleSections],
  );
  const sessionsOnly =
    navigationMode === 'sessions-only' ||
    (navigationMode === undefined &&
      resolvedConfig.navigationMode !== 'default');
  const showAgents = visibleSections.has('agents') && !sessionsOnly;
  const showSessions = visibleSections.has('sessions');
  const query = viewModel.searchQuery.trim().toLocaleLowerCase();
  const agents = useMemo(
    () =>
      viewModel.agents.items.filter(
        (item) =>
          !query ||
          `${item.name} ${item.description ?? ''}`
            .toLocaleLowerCase()
            .includes(query),
      ),
    [query, viewModel.agents.items],
  );
  const sessions = useMemo(
    () =>
      viewModel.sessions.items.filter(
        (item) => !query || item.title.toLocaleLowerCase().includes(query),
      ),
    [query, viewModel.sessions.items],
  );
  return (
    <Tabs
      value={sessionsOnly || !showAgents ? 'sessions' : viewModel.activeTab}
      onValueChange={(value) => {
        if (!sessionsOnly && (value === 'agents' || value === 'sessions'))
          onIntent?.({ type: 'change-tab', tab: value });
      }}
      className="h-full min-h-0"
    >
      <AgentWorkbenchSidebarContainer
        data-sidebar-width-percent={resolvedConfig.widthPercent}
        aria-label={messages.tabsLabel}
        header={visibleSections.has('header') ? header : undefined}
        search={
          visibleSections.has('search') ? (
            <AgentWorkbenchSidebarSearch
              value={viewModel.searchQuery}
              label={sessionsOnly ? messages.searchSessions : messages.search}
              className={classNames?.search}
              onChange={(query) => onIntent?.({ type: 'change-search', query })}
            />
          ) : undefined
        }
        actions={
          (showAgents && showSessions) || headerActions ? (
            <>
              {showAgents && showSessions ? (
                <TabsList
                  className={cn(
                    'grid min-w-0 flex-1 grid-cols-2 text-foreground',
                    classNames?.tabs,
                  )}
                  aria-label={messages.tabsLabel}
                >
                  <TabsTrigger value="agents">{messages.agents}</TabsTrigger>
                  <TabsTrigger value="sessions">
                    {messages.sessions}
                  </TabsTrigger>
                </TabsList>
              ) : null}
              {headerActions ? (
                <div className="flex shrink-0 items-center">
                  {headerActions}
                </div>
              ) : null}
            </>
          ) : undefined
        }
        footer={
          visibleSections.has('footer') &&
          ((!sessionsOnly && viewModel.capabilities.manageAgentSettings) ||
            footer) ? (
            <div
              className={cn('border-t border-border p-4', classNames?.footer)}
            >
              {!sessionsOnly && viewModel.capabilities.manageAgentSettings ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="h-10 w-full justify-start gap-2 px-3 text-sm"
                  onClick={() => onIntent?.({ type: 'open-agent-settings' })}
                >
                  <Settings className="size-4" aria-hidden="true" />
                  {messages.settings}
                </Button>
              ) : null}
              {footer}
            </div>
          ) : undefined
        }
        bodyScrollable={false}
        className={className}
        classNames={{
          root: classNames?.root,
          body: 'flex min-h-0 flex-1 flex-col p-0',
        }}
      >
        {showAgents ? (
          <TabsContent
            value="agents"
            data-agent-workbench-scroll-region
            className={cn(
              'mt-0 min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-4',
              classNames?.body,
            )}
          >
            {viewModel.capabilities.createAgent ? (
              <Button
                type="button"
                variant="ghost"
                className="mb-3 h-9 w-full justify-start gap-2 px-3 text-sm"
                onClick={() => onIntent?.({ type: 'create-agent' })}
              >
                <Plus className="size-4" aria-hidden="true" />
                {messages.newAgent}
              </Button>
            ) : null}
            <h3 className="mb-2 px-3 text-xs font-medium text-muted-foreground">
              {messages.allAgents}
            </h3>
            <CollectionState
              status={viewModel.agents.status}
              empty={messages.emptyAgents}
              noResults={messages.noResults}
              hasItems={agents.length > 0}
              query={query}
              error={viewModel.agents.error}
              onRetry={() => onIntent?.({ type: 'retry-agents' })}
            />
            <div className="space-y-1">
              {agents.map((item) => (
                <div
                  key={item.id}
                  aria-current={
                    item.id === viewModel.selectedAgentItem ? 'page' : undefined
                  }
                  className={cn(
                    'group flex w-full min-w-0 items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-left hover:bg-muted aria-[current=page]:border-primary aria-[current=page]:bg-muted',
                    classNames?.item,
                  )}
                >
                  <button
                    type="button"
                    title={item.name}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                    onClick={() =>
                      onIntent?.({ type: 'select-agent', itemId: item.id })
                    }
                  >
                    <span className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted">
                      <ItemAvatar item={item} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">
                        {item.name}
                      </span>
                      <span className="block truncate text-xs text-foreground/80">
                        {item.description || messages.noDescription}
                      </span>
                    </span>
                  </button>
                  {item.builtIn ? (
                    <span className="shrink-0 rounded-full bg-muted px-1.5 py-1 text-[10px] text-foreground">
                      {messages.builtIn}
                    </span>
                  ) : null}
                  {viewModel.capabilities.pinAgents ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={item.pinned ? messages.unpin : messages.pin}
                      className={cn(
                        'size-8 text-muted-foreground opacity-0 group-hover:opacity-100',
                        item.pinned && 'text-primary opacity-100',
                      )}
                      onClick={(event) => {
                        event.stopPropagation();
                        onIntent?.({
                          type: 'toggle-agent-pin',
                          itemId: item.id,
                          pinned: !item.pinned,
                        });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          onIntent?.({
                            type: 'toggle-agent-pin',
                            itemId: item.id,
                            pinned: !item.pinned,
                          });
                        }
                      }}
                    >
                      <Pin
                        className={cn(
                          'size-4',
                          item.pinned && 'fill-current',
                          item.pinPending && 'animate-pulse',
                        )}
                      />
                    </Button>
                  ) : null}
                </div>
              ))}
            </div>
          </TabsContent>
        ) : null}

        {showSessions ? (
          <TabsContent
            value="sessions"
            data-agent-workbench-scroll-region
            className={cn(
              'mt-0 min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain px-4 pb-4',
              classNames?.body,
            )}
          >
            {visibleSections.has('new-session') &&
            viewModel.capabilities.createSession ? (
              <AgentWorkbenchSidebarNewSessionButton
                label={messages.newSession}
                onClick={() => onIntent?.({ type: 'create-session' })}
              />
            ) : null}
            {!sessionsOnly && viewModel.capabilities.manageCapabilities ? (
              <Button
                type="button"
                variant="ghost"
                className="mb-3 h-9 w-full justify-start gap-2 px-3 text-sm"
                onClick={() => onIntent?.({ type: 'open-capabilities' })}
              >
                <Blocks className="size-4" aria-hidden="true" />
                {messages.capabilities}
              </Button>
            ) : null}
            <AgentWorkbenchSessionList
              sessions={sessions}
              selectedSessionId={viewModel.selectedSessionItem}
              status={viewModel.sessions.status}
              error={viewModel.sessions.error}
              query={query}
              emptyLabel={messages.emptySessions}
              noResultsLabel={messages.noResults}
              groupByMode={sessionsOnly}
              showHeading={!sessionsOnly}
              itemClassName={classNames?.item}
              capabilities={{
                pin: viewModel.capabilities.pinSessions,
                rename: viewModel.capabilities.renameSessions,
                delete: viewModel.capabilities.deleteSessions,
              }}
              onSelect={(sessionId) =>
                onIntent?.({ type: 'select-session', sessionId })
              }
              onTogglePin={(sessionId, pinned) =>
                onIntent?.({ type: 'toggle-session-pin', sessionId, pinned })
              }
              onRename={(sessionId, title) =>
                onIntent?.({ type: 'rename-session', sessionId, title })
              }
              onDelete={(sessionId) =>
                onIntent?.({ type: 'delete-session', sessionId })
              }
              onRetry={() => onIntent?.({ type: 'retry-sessions' })}
            />
          </TabsContent>
        ) : null}
      </AgentWorkbenchSidebarContainer>
    </Tabs>
  );
}
